import { useNavigate } from "react-router-dom";
import CountryFlag from "../transfer-windows/CountryFlag";
import NationalityFlags from "../common/NationalityFlags";
import { resolveNationalities } from "../../utils/nationalityFlags";
import { relativeActivity } from "../../utils/taskboardActivity";

// ──────────────────────────────────────────────────────────────────────
// One Taskboard tile = one PlayerFolder. Redesign: heartbeat is
// first-class (a live dot + "Last activity X"), the managing agent is a
// pill, the player's country shows a flag, and a folder with a stale
// Waiting item (older than the configured threshold) gets an amber
// attention marker so it stands out in the grid.
//
// Signals (lastActivityMs, isStale) are derived once in AgencyTaskboard
// and passed in, so the card stays presentational.
// Click anywhere -> /agency/folders/:id.
// ──────────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function AgentChip({ agent }) {
  if (!agent || !agent.name) return null;
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#F8FAFC] border border-[#EAECF0] rounded-full pl-1 pr-2.5 py-0.5 text-[12px] font-medium text-[#475467]">
      <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#FDEAD7] text-[#B93815] text-[8px] font-bold flex-none">
        {getInitials(agent.name)}
      </span>
      {agent.name}
    </span>
  );
}

const PlayerFolderCard = ({ folder, lastActivityMs = 0, isStale = false, staleDays = 0, now = Date.now() }) => {
  const navigate = useNavigate();
  const player = folder.player || {};
  const agent = folder.current_agent || {};
  const club = player.current_club || null;
  const entriesCount = folder.entries_count ?? (folder.entries || []).length;
  const linksCount = folder.links_count ?? (folder.links || []).length;

  return (
    <button
      type="button"
      onClick={() => navigate(`/agency/folders/${folder.id}`)}
      className={`text-left bg-white rounded-[14px] p-4 shadow-sm transition-all flex flex-col gap-3 hover:shadow-md hover:border-[#B8D4FF] ${
        isStale ? "border border-[#F4D8A6] ring-1 ring-[#FDEAD7]" : "border border-[#E4E9F0]"
      }`}
    >
      {/* Player header */}
      <div className="flex gap-3 items-center">
        {player.image ? (
          <img
            src={player.image}
            alt={player.name || ""}
            className="w-11 h-11 rounded-full object-cover flex-none"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="w-11 h-11 rounded-full bg-[#EAF2FF] text-[#1866CC] flex items-center justify-center text-[15px] font-bold flex-none">
            {getInitials(player.name)}
          </span>
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[15px] font-semibold text-[#101828] truncate">
              {player.name || "Unknown player"}
            </span>
            {/* All citizenships (L-FE-1): the ordered list when the roster carries more than one, else the single flag as before */}
            {resolveNationalities(player).length > 1 ? (
              <NationalityFlags nationalities={resolveNationalities(player)} className="h-[11px] w-[16px] rounded-[2px] object-cover" wrapperClassName="flex flex-wrap items-center gap-1" />
            ) : player.nationality ? (
              <CountryFlag country={player.nationality} width={16} height={11} />
            ) : null}
          </div>
          <span className="text-[13px] text-[#667085] truncate">
            {[player.position, club?.name].filter(Boolean).join(" · ") || "-"}
          </span>
        </div>
      </div>

      {/* Managing agent */}
      {agent.name && (
        <div className="flex">
          <AgentChip agent={agent} />
        </div>
      )}

      {/* Heartbeat + attention + counts */}
      <div className="border-t border-[#F2F4F7] pt-2.5 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full flex-none bg-[#12B76A]" aria-hidden="true" />
        <span className="text-[13px] font-semibold text-[#101828]">
          Last activity {relativeActivity(lastActivityMs, now)}
        </span>
        {isStale && (
          <span className="inline-flex items-center gap-1 bg-[#FFF8EB] text-[#B54708] text-[11px] font-semibold px-1.5 py-0.5 rounded-full" title="Waiting item needs attention">
            Waiting {staleDays > 0 ? `${staleDays}d` : ""}
          </span>
        )}
        <span className="ml-auto bg-[#F2F4F7] text-[#475467] text-[11.5px] font-semibold px-2 py-0.5 rounded-full">
          {entriesCount}
        </span>
        <span className="bg-[#F2F4F7] text-[#475467] text-[11.5px] font-semibold px-2 py-0.5 rounded-full">
          {linksCount} {linksCount === 1 ? "link" : "links"}
        </span>
      </div>
    </button>
  );
};

export default PlayerFolderCard;
